import {
    ChevronDownIcon,
    DownloadIcon,
    FileTextIcon,
    Loader2Icon,
    SheetIcon,
    type SvgIconComponent,
} from 'components/icons';
import { Button } from 'components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from 'components/ui/dropdown-menu';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import type { FigureRows } from 'types/aiChatFigure';
import { errorAlert } from 'utils/alert';
import { useAiChatTableDownloadTitle } from '../../../aiChatTableDownloadContext';
import {
    buildTableCsv,
    FORMAT_NAMES,
    saveTable,
    sheetName,
    titledTableFileName,
    type TableFileFormat,
} from '../../../utils/tableExport';
import { figureRowsTable } from './figureRows';

const FORMATS: { format: TableFileFormat; icon: SvgIconComponent }[] = [
    { format: 'xlsx', icon: SheetIcon },
    { format: 'csv', icon: FileTextIcon },
];

/**
 * One file at a time for a table: the format being generated, and the download that saves one. The Download menu and
 * the file card of the same table share it, so each says when the other is busy.
 */
export const useFigureDownload = (save: (format: TableFileFormat) => Promise<void>) => {
    const intl = useIntl();
    const [generating, setGenerating] = useState<TableFileFormat | null>(null);

    const download = async (format: TableFileFormat) => {
        if (generating) {
            return;
        }
        setGenerating(format);
        try {
            await save(format);
        } catch {
            errorAlert(intl.formatMessage({ id: 'ai-chat.table.download-failed' }, { format: FORMAT_NAMES[format] }));
        } finally {
            setGenerating(null);
        }
    };

    return { generating, download };
};

/**
 * A Figure's rows as a file and as copied CSV, the file named by the chat's title and the Figure's. `note` goes into
 * both, as a cut result's count does.
 */
export const useFigureExport = (title: string, rows: FigureRows | null | undefined, note: string | null) => {
    const intl = useIntl();
    const threadTitle = useAiChatTableDownloadTitle();
    const fallbackTitle = intl.formatMessage({ id: 'ai-chat.title' });
    const fileName = (format: TableFileFormat) => titledTableFileName(threadTitle ?? '', title, fallbackTitle, format);
    const notSet = intl.formatMessage({ id: 'ai-chat.figure.not-set' });
    const copyCsv = () => navigator.clipboard.writeText(rows ? buildTableCsv(figureRowsTable(rows, note, notSet)) : '');
    const { generating, download } = useFigureDownload(async (format) => {
        if (rows) {
            await saveTable(
                figureRowsTable(rows, note, notSet),
                format,
                fileName(format),
                sheetName(title, fallbackTitle)
            );
        }
    });

    return { fileName, copyCsv, generating, download };
};

interface Props {
    /** The table's column labels: they name the menu, so several tables in an answer tell apart. */
    columns: string[];
    generating: TableFileFormat | null;
    onDownload: (format: TableFileFormat) => void;
}

/** A Figure's Download menu: Excel or CSV of its rows. While a file is built it says so and keeps its focus. */
export function FigureDownload({ columns, generating, onDownload }: Props) {
    const intl = useIntl();
    const generatingLabel = generating
        ? intl.formatMessage({ id: 'ai-chat.table.generating' }, { format: FORMAT_NAMES[generating] })
        : '';

    return (
        <>
            <DropdownMenu>
                <DropdownMenuTrigger
                    render={
                        <Button
                            variant="ghost"
                            disabled={generating !== null}
                            focusableWhenDisabled
                            aria-label={
                                generatingLabel ||
                                intl.formatMessage(
                                    { id: 'ai-chat.table.label-columns' },
                                    { columns: intl.formatList(columns, { type: 'unit' }) }
                                )
                            }
                            className="text-meta text-foreground-secondary hover:bg-muted aria-expanded:bg-muted h-8 gap-1 rounded-lg ps-2 pe-1.5 font-medium aria-disabled:cursor-default aria-disabled:hover:bg-transparent"
                        />
                    }
                >
                    {generating ? (
                        <>
                            <span className="grid size-4 place-items-center">
                                <Loader2Icon className="size-3.5 motion-safe:animate-spin motion-reduce:animate-pulse" />
                            </span>
                            <span className="hidden pe-1 @md:inline">{generatingLabel}</span>
                        </>
                    ) : (
                        <>
                            <DownloadIcon className="size-4" />
                            <span className="hidden @md:inline">
                                {intl.formatMessage({ id: 'ai-chat.figure.download' })}
                            </span>
                            <ChevronDownIcon className="size-4 duration-(--duration-fast) ease-(--ease-smooth-out) group-aria-expanded/button:-scale-y-100 motion-safe:transition-transform" />
                        </>
                    )}
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-44">
                    {FORMATS.map(({ format, icon: Icon }) => (
                        <DropdownMenuItem key={format} className="text-meta" onClick={() => onDownload(format)}>
                            <Icon className="text-foreground-tertiary size-4" />
                            {intl.formatMessage({ id: `ai-chat.table.format-${format}` })}
                        </DropdownMenuItem>
                    ))}
                </DropdownMenuContent>
            </DropdownMenu>
            <span role="status" className="sr-only">
                {generatingLabel}
            </span>
        </>
    );
}
